import { DestroyRef, inject, signal, untracked } from '@angular/core';
import { LoggingService } from '@daisi/services/logging/logging.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Increment } from '@daisi/models/increment';
import { retry, Subscription, tap, timer } from 'rxjs';
import { EMPTY_FEATURE_COLLECTION } from '@daisi/models/feature-collection';
import { CombineService } from '@services/data/combine.service';
import { BannerService } from '@components/layout/banner.service';
import { FeatureCollection } from '@generated/backend-api/model/feature-collection';
import { DataStreamerService } from '@services/stomp/data-streamer-service';
import {
  fetchErrorBanner,
  fetchErrorBannerId,
  LastUpdateAgeMonitor,
  StalenessConfig,
} from '@services/data/last-update-age-monitor';

/** How long to wait before retrying a failed snapshot fetch (the websocket self-heals on its own). */
const SNAPSHOT_RETRY_DELAY_MS = 10_000;

/** How long to wait before resubscribing after the increment stream errored. */
const INCREMENT_RETRY_DELAY_MS = 5_000;

/**
 * Shared lifecycle for the http-snapshot + websocket-increment data sources. Concrete services
 * provide the streamer to use (Daisi or realtime), the stream to listen to, and the labels used in
 * the warning banners; {@link RealtimeDataService} and IncidentsDataService both extend this.
 */
export abstract class StreamingDataService<TStreamId extends string, T extends FeatureCollection> {
  protected readonly log = inject(LoggingService);
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly combineService = inject(CombineService);
  protected readonly bannerService = inject(BannerService);

  /** Streamer that serves this layer; extends the shared {@link DataStreamerService}. */
  protected abstract readonly dataStreamer: DataStreamerService<TStreamId>;
  protected abstract readonly STREAM_NAME: TStreamId;

  /** Phrase used in the fetch-error banner, e.g. 'de incidentdata', 'de bruggen'. */
  protected abstract readonly sourceLabel: string;

  /** When set, freshness of this layer is monitored and a banner is shown when it looks stale. */
  protected abstract readonly staleness?: StalenessConfig;

  readonly state = signal<T>(EMPTY_FEATURE_COLLECTION as unknown as T);

  private listening = false;
  private incrementErrorShown = false;
  private snapshotSubscription?: Subscription;
  private incrementSubscription?: Subscription;
  private freshnessMonitor?: LastUpdateAgeMonitor;

  startListening() {
    // The layer-toggle effect shares one activeMapOptions signal, so it re-runs (and re-calls this)
    // on every layer change. Guard against stacking duplicate subscriptions / retry loops.
    if (this.listening) {
      return;
    }
    this.listening = true;

    this.snapshotSubscription = this.dataStreamer
      .fetchSnapshot(this.STREAM_NAME)
      .pipe(
        // Warn on each failed attempt, then keep retrying until the snapshot loads, so the layer
        // recovers on its own once the backend is back (the websocket already reconnects itself).
        tap({ error: (e) => this.onStreamError(e) }),
        retry({ delay: () => timer(SNAPSHOT_RETRY_DELAY_MS) }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((snapshot) => {
        this.clearFetchErrorBanner();
        this.updateState(snapshot.data as unknown as T);

        // A failing increment stream used to be swallowed upstream (catchError -> EMPTY): the
        // subscription simply completed, the screen kept showing stale data and nothing said so.
        // Warn and resubscribe instead; listen() resends the subscribe headers with the last
        // received timestamp as stream offset, so nothing published in the meantime is lost.
        this.incrementSubscription = this.dataStreamer
          .onIncrement(this.STREAM_NAME, snapshot.timestamp)
          .pipe(
            tap({ error: (e) => this.onIncrementError(e) }),
            retry({ delay: () => timer(INCREMENT_RETRY_DELAY_MS) }),
          )
          .subscribe((increment) => {
            this.clearIncrementErrorBanner();
            this.incrementHandler(increment);
          });
        this.startFreshnessMonitor();
      });
  }

  stopListening() {
    this.listening = false;
    this.dataStreamer.stopListening(this.STREAM_NAME);
    this.snapshotSubscription?.unsubscribe();
    this.snapshotSubscription = undefined;
    this.incrementSubscription?.unsubscribe();
    this.incrementSubscription = undefined;
    this.incrementErrorShown = false;
    this.clearFetchErrorBanner();
    this.freshnessMonitor?.stop();
  }

  protected updateState(updatedState: T) {
    this.state.set(updatedState);
  }

  incrementHandler(increment: Increment) {
    if (increment.event.type === 'StreamingResetEvent') {
      // The backend posts a reset event before sending the new snapshot.
      // If we try to get a new snapshot immediately after receiving this event, we will get an old snapshot and after that
      // this reset event will be triggered again, resulting in 2 snapshots.
      // By waiting 500ms we give the backend some time to post the new snapshot.
      this.stopListening();
      setTimeout(() => {
        this.startListening();
      }, 500);
    } else {
      const updatedState = this.combineService.combine(
        this.state(),
        increment.event,
      ) as unknown as T;
      this.updateState(updatedState);
    }
  }

  private onStreamError(e: unknown) {
    // Keep the last known data on screen; just warn that the connection failed.
    this.log.error(`Error while streaming ${this.STREAM_NAME}`, e);
    this.bannerService.addBanner(fetchErrorBanner(this.STREAM_NAME, this.sourceLabel));
  }

  private onIncrementError(e: unknown) {
    this.incrementErrorShown = true;
    this.onStreamError(e);
  }

  /**
   * Clears the banner once increments flow again. Guarded by a flag because closeBanner() always
   * writes the banners signal, and this runs on every single incoming message.
   */
  private clearIncrementErrorBanner() {
    if (!this.incrementErrorShown) {
      return;
    }
    this.incrementErrorShown = false;
    this.clearFetchErrorBanner();
  }

  private clearFetchErrorBanner() {
    // Called from stopListening(), which the map runs inside its layer-toggle effect.
    // closeBanner reads *and* writes the banners signal, so it must stay off the reactive graph,
    // otherwise that effect starts tracking banners and re-runs itself forever (see
    // LastUpdateAgeMonitor.stop()/check() for the same guard).
    untracked(() => this.bannerService.closeBanner(fetchErrorBannerId(this.STREAM_NAME)));
  }

  private startFreshnessMonitor() {
    if (!this.staleness) {
      return;
    }
    this.freshnessMonitor ??= new LastUpdateAgeMonitor(
      this.STREAM_NAME,
      this.staleness,
      this.bannerService,
      this.destroyRef,
    );
    this.freshnessMonitor.start();
  }
}
